<template>
  <div
    :class="['smart-image', { 'show-skeleton': skeleton }]"
    ref="imageContainer"
  >
    <div class="error-container">
      <UiIcon
        name="image-placeholder-outlined"
        size="42px"
        color-fill="var(--color-base-grey-50)"
      />
    </div>

    <picture class="picture">
      <source
        :key="index"
        :srcset="source.srcset"
        :media="source.media"
        v-for="(source, index) in sources"
      />
      <img
        :class="['image', { broken: !src }]"
        :alt="alt"
        :fetchPriority="fetchPriority"
        :loading="loadingType"
        :role="alt ? 'img' : 'presentation'"
        :src="src"
        onload="this.classList.add('loaded'); this.classList.remove('broken');"
        onerror="this.classList.add('broken'); this.classList.remove('loaded');"
        data-allow-mismatch="class"
      />
    </picture>
  </div>
</template>

<script lang="ts" setup>
import {
  reactive,
  onMounted,
  computed,
  useTemplateRef,
  useSSRContext,
} from 'vue'
import { joinURL } from 'ufo'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'
import {
  sizeBreakpointsMobileMaxWidth,
  sizeBreakpointsTabletMaxWidth,
  sizeBreakpointsDesktopMaxWidth,
  sizeBreakpointsWidescreenMaxWidth,
  sizeBreakpointsFullhdMinWidth,
  // @ts-ignore - ignoring as it is a js file with no types declarations
} from '@sephora-asia/ui-design-tokens/js/tokens'
import type { PropsSmartImage, States, Edits } from './types'

const imageContainer = useTemplateRef('imageContainer')

const {
  fetchPriority = 'auto',
  lazyLoading = true,
  objectFit = 'cover',
  skeleton = true,
  alt = '',
  preload = false,
  srcTablet = '',
  srcMobile = '',
  ...props
} = defineProps<PropsSmartImage>()

const loadingType =
  fetchPriority === 'high' || lazyLoading === false ? 'eager' : 'lazy'

// TODO: import.meta.env.SSR is available in latest vite version
const isServer = typeof window === 'undefined'

const aspectRatioForMobile = computed(
  () => props.aspectRatioMobile || props.aspectRatio
)

const aspectRatioForTablet = computed(
  () => props.aspectRatioTablet || props.aspectRatio
)

const imageHeight = computed(() => props.aspectRatio === 'auto' || '100%')
const imageWidth = computed(() => props.aspectRatio === 'auto' || '100%')

const state: States = reactive({
  containerWidth: 0,
  containerHeight: 0,
})

const breakpoints = [
  sizeBreakpointsMobileMaxWidth,
  sizeBreakpointsTabletMaxWidth,
  sizeBreakpointsDesktopMaxWidth,
  sizeBreakpointsWidescreenMaxWidth,
] as string[]

const sources = computed(() =>
  props.src
    ? breakpoints.map(width => {
        let url = props.src

        if (srcTablet && width === sizeBreakpointsTabletMaxWidth)
          url = srcTablet

        if (srcMobile && width === sizeBreakpointsMobileMaxWidth)
          url = srcMobile

        return {
          srcset: `${imageSrc(
            { width: parseInt(width), height: undefined },
            url
          )} ${parseInt(width)}w`,
          media: `(max-width: ${width})`,
        }
      })
    : []
)

// Add preload links to head
if (props.src && preload && isServer) {
  try {
    const ssrCtx = useSSRContext?.()

    if (typeof ssrCtx?.head?.push === 'function')
      ssrCtx.head.push({
        link: [
          ...sources.value.map((source, index) => ({
            rel: 'preload',
            as: 'image',
            href: source.srcset.split(' ')[0],
            media:
              (index
                ? `(min-width: ${parseInt(breakpoints[index - 1]) + 1}px) and `
                : '') + source.media,
          })),
          {
            rel: 'preload',
            as: 'image',
            href: props.src,
            media: `(min-width: ${sizeBreakpointsFullhdMinWidth})`,
          },
        ],
      })
  } catch (e) {
    console.error(
      'Error while adding preload links for image source',
      props.src,
      e
    )
  }
}

onMounted(() => {
  if (imageContainer.value) {
    state.containerHeight = imageContainer.value.clientHeight
    state.containerWidth = imageContainer.value.clientWidth
  }
})

function imageSrc(
  size: null | { height: undefined | number; width: undefined | number },
  src: string
): string {
  const defaultBaseUrl = src
  const edits: Edits = { resize: {} }

  if (size) {
    edits.resize.height = size.height
    edits.resize.width = size.width
  } else {
    /* TODO: Re-evaluate this logic
    1. Custom width and height settings may unintentionally crop the image. Recommended to use width only.
    2. Pixel-based resizing may not be accurate due to DPR (css pixel != device pixel) and hence higher DPI needs to be considered.
    3. Container dimensions are only available after mounting, causing the image to be refetched.
    */
    edits.resize.height = state.containerHeight
    edits.resize.width = state.containerWidth
  }

  try {
    const url = new URL(src)
    const { origin, pathname } = url

    /*only append the resize params when
      1. the path name never contains base64 encoded hash
      2. origin is from image-optimizer
      3. when width > 0 or else image-optimizer returns error
      4. Image optimizer only supports jpeg, png, webp and tiff to have custom edits
      5. When the src does not contain base64 encoded edits already
    */
    if (
      edits?.resize?.width &&
      /^https:\/\/image-optimizer.*\.(jpe?g|png|webp|tiff)(?:\?.*)?$/.test(
        src
      ) &&
      !isValidBase64Json(pathname.split('/').at(1))
    ) {
      const options = { edits }
      const stringifiedOptions = JSON.stringify(options)
      const base64EncodedOptions = isServer
        ? Buffer.from(stringifiedOptions).toString('base64') // cjs
        : window.btoa(stringifiedOptions) // esm

      return joinURL(origin, base64EncodedOptions, pathname)
    } else {
      return defaultBaseUrl
    }
  } catch {
    return defaultBaseUrl
  }
}

function isValidBase64Json(str?: string | null) {
  try {
    if (!str) return false

    const decoded = isServer
      ? Buffer.from(str, 'base64').toString('utf-8')
      : window.atob(str)

    const json = JSON.parse(decoded)
    return json && typeof json === 'object'
  } catch (e) {
    return false
  }
}
</script>

<style lang="scss" scoped>
.smart-image {
  aspect-ratio: v-bind(aspectRatio);
  display: block;
  position: relative;

  &.show-skeleton:not(:has(img.loaded)):not(:has(img.broken)) {
    font-size: 0; // Firefox shows alt text while image is loading
    @extend .skeleton-loader;
  }

  .picture {
    display: flex;
    width: 100%;
    height: 100%;
  }

  &:has(img.broken) {
    .error-container {
      display: flex;
    }

    .picture {
      display: none;
    }
  }

  &:has(img.loaded) {
    .error-container {
      display: none;
    }
  }

  .error-container {
    background-color: var(--color-base-grey-10);
    aspect-ratio: v-bind(aspectRatio);
    width: 100%;
    height: 100%;
    display: none;
    justify-content: center;
    align-items: center;
  }

  .image {
    aspect-ratio: v-bind(aspectRatio);
    object-fit: v-bind(objectFit);
    max-width: 100%;
    max-height: 100%;
    width: v-bind(imageWidth);
    height: v-bind(imageHeight);
  }
}

@include tablet {
  .smart-image {
    aspect-ratio: v-bind(aspectRatioForTablet);

    .image,
    .image-placeholder {
      aspect-ratio: v-bind(aspectRatioForTablet);
    }
  }
}

@include mobile {
  .smart-image {
    aspect-ratio: v-bind(aspectRatioForMobile);

    .image,
    .image-placeholder {
      aspect-ratio: v-bind(aspectRatioForMobile);
    }
  }
}
</style>
